// A single isometric cube, rendered as one inline SVG. This is the shared
// building block every "mark" component on the redesigned page uses (row
// markers, section-level nests, the closing cube, etc). The geometry comes
// from src/lib/iso.ts so it stays byte-identical to the hero Scene's cubes;
// this file only owns how a cube LOOKS as a small, static glyph — no motion
// of its own, no observers, no effects. (The dashed tone's crawl and the
// closing cube's face cycle are CSS animations, declared in index.css and
// CubeGlyph.css; whoever mounts a cycling glyph decides when it may play.)
//
// Mounted by: MintNest, ClosingCube, and directly by the row-scaffolding
// sections (WhatWeDo, HowWeWork) wherever the design calls for one cube.

import { useId } from 'react';
import { cubeEdges, cubeFaces, cubeViewBox } from '../../lib/iso';
import './CubeGlyph.css';

export type CubeTone = 'mint' | 'ice' | 'peri' | 'dash';
export type CubeFace = 'top' | 'right' | 'left';

// Clockwise around the cube's centre vertex, as the viewer sees it: the top
// face first, then the front-right, then the front-left. "How we work"
// counts its three points in this order (one lit face per point) and the
// closing cube cycles its faces in the same order — one shared list, so the
// two can never disagree about which face is "1".
export const FACE_ORDER: CubeFace[] = ['top', 'right', 'left'];

// Face gradient stops, copied verbatim from Scene.tsx's <Grad> definitions
// for each tone. Scene.tsx uses the same fills in both light and dark
// theme, so CubeGlyph does the same here.
const FACE_STOPS: Record<Exclude<CubeTone, 'dash'>, { top: [string, string]; right: [string, string]; left: [string, string] }> = {
  peri: { top: ['#eef3fd', '#d3ddf5'], right: ['#c6d0ec', '#a9b7e0'], left: ['#aab8de', '#8c9cce'] },
  mint: { top: ['#ddf0f5', '#c2e2ec'], right: ['#bfe1ec', '#9ccbdc'], left: ['#a4cfdd', '#82b4c6'] },
  ice: { top: ['#f0f5fe', '#dfe9f9'], right: ['#d3e0f5', '#bccfef'], left: ['#c0cfea', '#a8bce0'] },
};

// The geometry helpers work in fixed "world" units and any altitude scales
// cleanly through the viewBox, so a constant altitude is used internally —
// the rendered pixel size is controlled purely by the svg width/height.
const ALTITUDE = 60;

export function CubeGlyph({
  tone,
  size = 28,
  className,
  face,
  cycle = false,
}: {
  tone: CubeTone;
  size?: number;
  className?: string;
  // Dashed tone only: which face is lit (filled with --dash-face). The
  // "How we work" markers use this to count 1 / 2 / 3.
  face?: CubeFace;
  // Dashed tone only: light the faces one at a time, top, right, left,
  // forever (CSS, see CubeGlyph.css). At rest, and under reduced motion,
  // the `face` (default top) is the one lit.
  cycle?: boolean;
}) {
  // useId keeps every glyph instance's gradient/mask ids unique, so many
  // glyphs on one page never collide even though they share <defs> shapes.
  const reactId = useId();
  const gradientId = (face: string) => `cube-glyph-${reactId}-${face}`;

  const box = cubeViewBox(ALTITUDE);
  const faces = cubeFaces(box.origin, ALTITUDE);
  const edges = cubeEdges(box.origin, ALTITUDE);

  const classes = ['cube-glyph', cycle ? 'cube-glyph--cycle' : null, className].filter(Boolean).join(' ');

  if (tone === 'dash') {
    const lit = face ?? 'top';
    const showFaces = face !== undefined || cycle;
    return (
      <svg
        className={classes}
        width={size}
        height={size}
        viewBox={`0 0 ${box.width} ${box.height}`}
        aria-hidden="true"
      >
        {/* the top face reads as a faint plane under the dashed edges,
            exactly as Scene.tsx's DashedCube treats it — but only on a
            plain dashed glyph. A counting or cycling cube lights exactly
            one face, and the faint plane next to a lit face read as a
            second fill (the owner caught it), so it is dropped there. */}
        {!showFaces && <polygon points={faces.top} fill="var(--dash-top)" opacity="0.18" />}
        {/* the lit face(s), under the edges so the dashes stay on top. All
            three are in the DOM whenever any is, so the cycle has something
            to light — the unlit ones sit at opacity 0. */}
        {showFaces &&
          FACE_ORDER.map((f) => (
            <polygon
              key={f}
              data-face={f}
              points={faces[f]}
              fill="var(--dash-face)"
              className={f === lit ? 'cube-glyph__face cube-glyph__face--lit' : 'cube-glyph__face'}
            />
          ))}
        {edges.map(([from, to], index) => (
          <line
            key={index}
            x1={from.x}
            y1={from.y}
            x2={to.x}
            y2={to.y}
            stroke="var(--dash-edge)"
            strokeWidth="1.5"
            strokeDasharray="var(--seam-dash)"
            strokeLinecap="round"
            className="crawl"
          />
        ))}
      </svg>
    );
  }

  const stops = FACE_STOPS[tone];
  const topId = gradientId('top');
  const rightId = gradientId('right');
  const leftId = gradientId('left');

  return (
    <svg
      className={classes}
      width={size}
      height={size}
      viewBox={`0 0 ${box.width} ${box.height}`}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={topId} x1="0" y1="0" x2="1" y2="0.6">
          <stop offset="0" stopColor={stops.top[0]} />
          <stop offset="1" stopColor={stops.top[1]} />
        </linearGradient>
        <linearGradient id={rightId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={stops.right[0]} />
          <stop offset="1" stopColor={stops.right[1]} />
        </linearGradient>
        <linearGradient id={leftId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={stops.left[0]} />
          <stop offset="1" stopColor={stops.left[1]} />
        </linearGradient>
      </defs>
      <polygon points={faces.left} fill={`url(#${leftId})`} stroke="var(--cube-line)" strokeWidth="1" strokeLinejoin="round" />
      <polygon points={faces.right} fill={`url(#${rightId})`} stroke="var(--cube-line)" strokeWidth="1" strokeLinejoin="round" />
      <polygon points={faces.top} fill={`url(#${topId})`} stroke="var(--cube-line)" strokeWidth="1" strokeLinejoin="round" />
    </svg>
  );
}
